import React, { useCallback, useEffect, useState } from "react";
import { Building2, HardDrive, Loader2, Save, X } from "lucide-react";

/* ===================== COMPONENTS ===================== */
import Modal from "../Modal/Modal";
import Input from "../Input/Input";

/* ===================== SERVICES ===================== */
import { apiGet, apiPut } from "../../apiService";
import { getErrorMessage } from "../../utils/errorHelper";

/* ===================== STYLES ===================== */
import styles from "./StorageModals.module.scss";

const GB_TO_BYTES = 1_000_000_000;

const formatGB = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) {
    return "--";
  }
  return `${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 2,
  }).format(Number(value))} GB`;
};

const EditStorageModal = ({ open, orgId, onClose, onSuccess, onError }) => {
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [storage, setStorage] = useState(null);
  const [storageLimitGb, setStorageLimitGb] = useState("");
  const [error, setError] = useState("");

  const fetchStorage = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const res = await apiGet("catalog", `/billing/storage/orgs/${orgId}`);
      if (res?.status !== "success") {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to load storage details",
          }),
        );
      }
      setStorage(res.data || null);
      setStorageLimitGb(
        res.data?.storageLimitGb === null ||
          res.data?.storageLimitGb === undefined
          ? ""
          : String(res.data.storageLimitGb),
      );
    } catch (err) {
      if (typeof onError === "function") {
        onError(getErrorMessage(err, "Failed to load storage details"));
      }
      onClose?.();
    } finally {
      setLoading(false);
    }
  }, [orgId, onError, onClose]);

  useEffect(() => {
    if (open) {
      setStorage(null);
      setStorageLimitGb("");
      setError("");
      fetchStorage();
    }
  }, [open, fetchStorage]);

  const handleChange = (e) => {
    const raw = e.target.value;
    // Allow numeric input including decimals
    if (raw === "" || /^[0-9]*\.?[0-9]*$/.test(raw)) {
      setStorageLimitGb(raw);
      setError("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const num = Number(storageLimitGb);
    if (storageLimitGb === "" || Number.isNaN(num) || num <= 0) {
      setError("Enter a valid storage limit greater than 0 GB");
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        storageLimitGb: num,
        storageLimitBytes: Math.round(num * GB_TO_BYTES),
      };
      const res = await apiPut(
        "catalog",
        `/billing/storage/orgs/${orgId}`,
        payload,
      );
      if (res?.status === "success") {
        onSuccess?.(res?.message || "Storage limit updated");
        onClose?.();
      } else {
        throw new Error(
          JSON.stringify({
            message: res?.message || "Failed to update storage limit",
          }),
        );
      }
    } catch (err) {
      const message = getErrorMessage(err, "Failed to update storage limit");
      setError(message);
      onError?.(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <Modal title="Update Storage Limit" onClose={onClose}>
      {loading ? (
        <div className={styles["storage-modal__loading"]}>
          <Loader2 className={styles["storage-modal__spin"]} size={28} />
          <span>Loading storage details…</span>
        </div>
      ) : storage ? (
        <div className={styles["storage-modal__container"]}>
          {/* ORG SUMMARY */}
          <div className={styles["storage-modal__section"]}>
            <div className={styles["storage-modal__section-header"]}>
              <div className={styles["storage-modal__icon-wrapper"]}>
                <Building2 size={18} strokeWidth={2.5} />
              </div>
              <h3 className={styles["storage-modal__section-title"]}>
                {storage.name || storage.companyName || "Organization"}
              </h3>
            </div>

            <div className={styles["storage-modal__grid"]}>
              <div className={styles["storage-modal__field"]}>
                <span className={styles["storage-modal__field-label"]}>
                  Current Limit
                </span>
                <span className={styles["storage-modal__field-value"]}>
                  {formatGB(storage.storageLimitGb)}
                </span>
              </div>
              <div className={styles["storage-modal__field"]}>
                <span className={styles["storage-modal__field-label"]}>
                  Used
                </span>
                <span className={styles["storage-modal__field-value"]}>
                  {formatGB(storage.storageUsedGb)}
                </span>
              </div>
            </div>
          </div>

          {/* FORM */}
          <form
            onSubmit={handleSubmit}
            noValidate
            className={styles["storage-modal__form"]}
          >
            <Input
              label="New Storage Limit (GB)"
              type="text"
              inputMode="decimal"
              name="storageLimitGb"
              placeholder="e.g. 50"
              value={storageLimitGb}
              onChange={handleChange}
              error={error}
              required
            />

            <p className={styles["storage-modal__hint"]}>
              <HardDrive size={14} strokeWidth={2.5} />
              <span>
                {storageLimitGb && !Number.isNaN(Number(storageLimitGb))
                  ? `Equivalent to ${Math.round(
                      Number(storageLimitGb) * GB_TO_BYTES,
                    ).toLocaleString("en-IN")} bytes`
                  : "Enter a value to preview the byte equivalent"}
              </span>
            </p>

            <div className={styles["storage-modal__actions"]}>
              <button
                type="button"
                className={`${styles["storage-modal__btn"]} ${styles["storage-modal__btn--secondary"]}`}
                onClick={onClose}
                disabled={submitting}
              >
                <X size={16} strokeWidth={2.5} />
                Cancel
              </button>

              <button
                type="submit"
                className={`${styles["storage-modal__btn"]} ${styles["storage-modal__btn--primary"]}`}
                disabled={submitting}
              >
                <Save size={16} strokeWidth={2.5} />
                {submitting ? "Updating…" : "Update Limit"}
              </button>
            </div>
          </form>
        </div>
      ) : null}
    </Modal>
  );
};

export default EditStorageModal;
